<template>
    <div id="receipt">
        <div v-if="loadingReceipt" class="text-center">
            <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
        </div>
        <div v-else class="container">
            <header v-if="receiptHtml">
                <LocationLogo v-if="location.logo_url" style="width: 81px; height: 81px; margin: auto;"
                    :logo="location.logo_url" :shape="location.logo_shape || 'circle'" borderColor="#2A2927" />
                <br />
                <h3>{{ location.name }}</h3>
                <p>{{ text.receiptMessage }}</p>
            </header>

            <div v-if="receiptHtml">
                <div v-html="localizedReceiptHtml"></div>
            </div>

            <div v-else class="block">
                <p class="text-center strong">Receipt not found</p>
                <p class="text-center quiet">Please check with an employee to make sure they have your order.</p>
            </div>

            <div class="block" v-if="receiptHtml">
                <p class="text-center strong">{{ text.ratingPrompt }}</p>
                <div class="rating" :class="{ 'loading': loadingRating }">
                    <span v-for="star in 5" :key="star" class="star" @click="!loadingRating && submitRating(star)"
                        :class="{ 'rated': star <= currentRating }"></span>
                </div>
                <br />
                <p class="text-center quiet">Tap a star to rate.</p>
            </div>
            <footer v-if="receiptHtml"><span class="s4-logo"></span></footer>
        </div>
    </div>

</template>

<script>
import LocationLogo from '@/components/global/LocationLogo.vue';
import { mapState, mapActions } from 'vuex';

export default {
    components: {
        LocationLogo,
    },
    data() {
        return {
            currentRating: 0,
        };
    },
    computed: {
        ...mapState('receipt', ['receipt', 'loadingReceipt', 'receiptError', 'loadingRating']),
        location() {
            return this.receipt ? this.receipt.location : {};
        },
        text() {
            return this.receipt ? this.receipt.text : {};
        },
        receiptHtml() {
            return this.receipt ? this.receipt.html : '';
        },
        localizedReceiptHtml() {
            if (!this.receiptHtml) return '';
            return this.convertUtcTimesToLocal(this.receiptHtml);
        },
    },
    async created() {
       await this.fetchReceipt(this.$route.params.ref);
    },
    methods: {
        ...mapActions('receipt', ['fetchReceipt', 'submitReview']),
        async submitRating(rating) {
            const review = {
                rating,
            };
            const success = await this.submitReview({ orderRef: this.$route.params.ref, review });
            if (success) {
                this.currentRating = rating;
            } else {
                this.currentRating = 0;
            }
        },
        convertUtcTimesToLocal(html) {
            // Create a temporary DOM element to parse the HTML
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = html;
            
            // Find all elements with transaction-date class
            const dateElements = tempDiv.querySelectorAll('.transaction-date');
            
            dateElements.forEach(element => {
                const dateText = element.textContent.trim();
                try {
                    // Try to parse the date - this handles various date formats from the backend
                    const parsedDate = this.parseReceiptDate(dateText);
                    if (parsedDate) {
                        // Format the date in the user's local timezone
                        const localDate = parsedDate.toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                        });
                        element.textContent = localDate;
                    }
                } catch (error) {
                    console.warn('Could not parse receipt date:', dateText, error);
                    // Keep original date if parsing fails
                }
            });
            
            return tempDiv.innerHTML;
        },
        parseReceiptDate(dateString) {
            // Common date formats that might be in the receipt:
            // "Jan 15, 2024, 2:30 PM"
            // "Jan 15, 2024 2:30 PM" 
            // "01/15/2024 2:30 PM"
            // "2024-01-15T14:30:00Z"
            
            // First, try parsing as ISO string (if backend sends UTC timestamps)
            let date = new Date(dateString);
            if (!isNaN(date.getTime())) {
                return date;
            }
            
            // Try parsing various formats by adding UTC indicator
            // This assumes the backend date is in UTC but doesn't specify timezone
            const patterns = [
                // Format: "Jan 15, 2024, 2:30 PM" or "Jan 15, 2024 2:30 PM"
                /^(\w{3})\s+(\d{1,2}),\s+(\d{4})[,\s]+(\d{1,2}):(\d{2})\s+(AM|PM)$/i,
                // Format: "01/15/2024 2:30 PM"
                /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})\s+(AM|PM)$/i
            ];
            
            for (const pattern of patterns) {
                const match = dateString.match(pattern);
                if (match) {
                    if (pattern.toString().includes('\\w{3}')) {
                        // Month name format
                        const [, month, day, year, hour, minute, ampm] = match;
                        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                                          'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                        const monthIndex = monthNames.indexOf(month);
                        if (monthIndex !== -1) {
                            let hour24 = parseInt(hour);
                            if (ampm.toLowerCase() === 'pm' && hour24 !== 12) hour24 += 12;
                            if (ampm.toLowerCase() === 'am' && hour24 === 12) hour24 = 0;
                            
                            // Create date assuming UTC timezone
                            date = new Date(Date.UTC(parseInt(year), monthIndex, parseInt(day), hour24, parseInt(minute)));
                            if (!isNaN(date.getTime())) return date;
                        }
                    } else {
                        // Numeric format MM/DD/YYYY
                        const [, month, day, year, hour, minute, ampm] = match;
                        let hour24 = parseInt(hour);
                        if (ampm.toLowerCase() === 'pm' && hour24 !== 12) hour24 += 12;
                        if (ampm.toLowerCase() === 'am' && hour24 === 12) hour24 = 0;
                        
                        // Create date assuming UTC timezone
                        date = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day), hour24, parseInt(minute)));
                        if (!isNaN(date.getTime())) return date;
                    }
                }
            }
            
            return null;
        },
    },
};
</script>
<style>
body {
    font-size: 16px !important;
}

#receipt {
    max-width: 40rem;
    margin: 0 auto;

}

#receipt header {
    align-items: center;
    text-align: center;
    margin: 1.5rem;
}

#receipt header img {
    width: 81px;
    height: 81px;
    margin-bottom: 0.5rem;
}

#receipt .block {
    padding: 1.125rem;
    margin-bottom: 0.75rem;

    background: #FFFFFF;
    box-shadow: 0px 1px 10px rgba(0, 0, 0, 0.08);
    border-radius: 0.75rem;
}

#receipt .block .customer-info,
#receipt .block .payment-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    color: #475467;
    font-size: 0.875rem;
}

#receipt .strong {
    font-weight: 700;
}

#receipt .quiet {
    font-weight: 100;
    font-size: 0.75rem;
    color: #6c757d;
}

#receipt ul.items {
    list-style: none;
    padding: 1rem;
    margin: 0;

}

#receipt ul.items li {
    margin: 0;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 0.5rem 0;
}

#receipt ul.items .quantity {
    flex: 0 0 3rem;
    text-align: left;
}

#receipt ul.items .name {
    flex: 1;
    font-weight: 700;
    margin-left: 1rem;
}

#receipt ul.items .choices {
    font-size: 0.875rem;
    font-weight: 100;
    color: #6c757d;
    margin: 0;
}

#receipt ul.items .price {
    flex: 0 0 4rem;
    text-align: right;
}

#receipt .totals {
    margin-top: 1rem;
    padding: 1rem;
    border-top: 1px solid #EAECF0;

}

#receipt .totals div {
    display: flex;
    justify-content: space-between;
    margin-bottom: 0.5rem;
}

#receipt .card-type {
    display: flex;
    gap: 0.5rem;
}


#receipt .card-badge {
    width: 32px;
    height: 24px;
    background-size: contain;
    background-repeat: no-repeat;
    border: none;
    background-image: url('@/assets/cards/other.svg');
}

#receipt .card-badge.ax {
    background-image: url('@/assets/cards/amex.svg');
}

#receipt .card-badge.ns {
    background-image: url('@/assets/cards/dsc.svg');
}

#receipt .card-badge.mc {
    background-image: url('@/assets/cards/mc.svg');
}

#receipt .card-badge.vs {
    background-image: url('@/assets/cards/vs.svg');
}

#receipt footer {
    margin: 2.5rem 0;
}

#receipt footer .s4-logo {
    margin: auto;
    display: block;
    background-size: contain;
    background-repeat: no-repeat;
    height: 2.625rem;
    width: 6.857rem;
    background-image: url('@/assets/powered-by-s4.svg');
}

#receipt .rating {
    display: flex;
    justify-content: center;
    gap: 0.5rem;
}

#receipt .star {
    cursor: pointer;
    width: 2.5rem;
    height: 2.5rem;
    background-size: contain;
    background-repeat: no-repeat;
    background-image: url('@/assets/star.svg');
}

#receipt .star.rated {
    background-image: url('@/assets/star-filled.svg');
}

#receipt .rating.loading .star {
    opacity: 0.5;
    pointer-events: none;
}
</style>
